import {
    CellContext,
    DisplayColumnDef,
    Row,
    RowData,
    TableOptions,
    AccessorKeyColumnDef,
    AccessorFnColumnDef
} from "@tanstack/react-table";
import React from "react";
import Block from "../Block";

export type TTableVariant = "default" | "inset";

export type DefaultColumns = "expand" | "select";

export type IColumnName<T extends string> = T | DefaultColumns;

export type IColumnSizing<TColumnName extends string> = Record<TColumnName, number>;

export interface IItem {
    id: number | string;
}

export type TRow<TItem extends IItem> = Row<TItem>;

export type TCellContext<TItem extends IItem, TValue = any> = CellContext<TItem, TValue>;

export type TEnableRowSelection<TItem extends IItem> = TableOptions<TItem>["enableRowSelection"];

export type TGetRowProps<TItem extends IItem> = (row: TRow<TItem>) => React.HTMLAttributes<HTMLTableRowElement>;

export type TSorting = "asc" | "desc" | undefined;

export type TSortingState<TColumnName extends string> = { [key in TColumnName]?: TSorting };

export type ITableProps<TItem extends IItem, TColumnName extends string> = React.ComponentProps<typeof Block> & {
    columnDefinitions: IColumnDefinition<TItem, IColumnName<TColumnName>>[];
    items: TItem[];
    trackName?: string;
    trackData?: object;
    emptyState?: React.ReactNode;
    loading?: boolean;
    footer?: React.ReactNode;
    rowHeight?: number | string;
    /**
     * Define a fixed height for the table. This does not take the Block header into consideration.
     *
     * If value is provided is a number, it will default to `px` unit.
     */
    tableHeight?: number | string;
    /**
     * Define a dynamic height for the table based on content. This does not take the Block header into consideration.
     *
     * If value is provided is a number, it will default to `px` unit.
     */
    tableMaxHeight?: number | string;
    renderExtraContent?: (item: TItem) => React.ReactNode;
    /**
     * When using this prop make sure to mount <Table.BulkActions.Portal/> in your app, as it is rendered outside of the table
     *
     * Also, it is better to use <Table.BulkActions> and <Table.BulkActions.Item/> for actions as they have design system styles
     */
    renderBulkActions?: (selectedItems: TItem[]) => React.ReactNode;
    getRowLink?: (item: TItem) => string;
    columnOrder?: IColumnName<TColumnName>[];
    visibleColumns?: IColumnName<TColumnName>[] | Partial<Record<IColumnName<TColumnName>, boolean>>;
    enableRowSelection?: TEnableRowSelection<TItem>;
    getRowProps?: TGetRowProps<TItem>;
    getRowTrackData?: (row: Row<TItem>) => object;
    disableColumnAutoSizing?: boolean;
    selectedItems?: TItem[];
    onRowSelectionChange?: (selectedItems: TItem[]) => void;
    variant?: TTableVariant;
    tableHead?: boolean;
    sorting?: TSortingState<TColumnName>;
    onSortingChange?: (sorting: TSortingState<TColumnName>) => void;
};

type TSharedColumnIdentifiers = "header" | "cell" | "size" | "minSize" | "maxSize" | "meta" | "enableSorting";

export type IColumnDefinition<TItem extends IItem, TColumnName extends string> = (
    | Pick<DisplayColumnDef<TItem, any>, TSharedColumnIdentifiers>
    | Pick<AccessorKeyColumnDef<TItem, any>, TSharedColumnIdentifiers | "accessorKey">
    | Pick<AccessorFnColumnDef<TItem, any>, TSharedColumnIdentifiers | "accessorFn">
) & {
    id: IColumnName<TColumnName>;
};

declare module "@tanstack/table-core" {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    interface ColumnMeta<TData extends RowData, TValue> {
        actions?: boolean;
        cellClassName?: string;
        headerClassName?: string;
    }
}
